import { useEffect, useMemo, useState } from "react";
import { useLanguage } from "../../../../../stores/LanguageStore";
import { useFlowStore } from "../../../../../stores/WorkflowStore";
import {
  Anchor,
  Blockquote,
  Button,
  Group,
  Paper,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core";
import { getNodeDetails } from "../flow/flowSettings";
import EmailPreviewer from "../../../../../components/View/EmailPreviewer";
import MessagePreviewer from "../../../../../components/View/MessagePreviewer";
import SenderVisual from "../flow/nodes/SenderVisual";
import { getFirstPageContacts } from "../../../../../api/ContactAPI";
import ContactPreview from "./ContactPreview";
import { useListStore } from "../../../../../stores/ListStore";
import { useCampaignStore } from "../../../../../stores/CampaignStore";
import {
  filterActionNodes,
  getWorkflowBranches,
} from "../../../../../functions/utilFunctions";
import SmvPreviewer from "../../../../../components/View/SmvPreviewer";
import MessageVocalPreviewer from "../../../../../components/View/MessageVocalPreviewer";

const Review = ({ setActive }) => {
  const lang = useLanguage((s) => s.language);
  const nodes = useFlowStore((s) => s.nodes);
  const programData = useCampaignStore((s) => s.programData);
  const [contact, setContact] = useState(null);
  const [contacts, setContacts] = useState([]);
  const lists = useListStore((s) => s.lists);

  const branchedActionNodes = useMemo(() => {
    // If workflow has multiple head
    if (nodes[0]?.data?.actionType === "dummy") {
      return getWorkflowBranches(nodes);
    }

    return [filterActionNodes(nodes)];
  }, [nodes]);

  const branchedEventNodes = useMemo(() => {
    // If workflow has multiple head
    if (nodes[0]?.data?.actionType === "dummy") {
      return getWorkflowBranches(nodes, true);
    }

    return [nodes.filter((x) => x?.data?.actionType === "event")];
  }, [nodes]);

  const [selected, setSelected] = useState(null);
  const preview = useMemo(() => {
    if (selected?.data.actionType === "email") {
      return (
        <EmailPreviewer
          key={selected?.id}
          model_id={selected?.data?.model_id}
          iframeHeight={"100%"}
          signatureId={selected?.data?.signature_id}
          contact={contact}
          paperStyle={{
            radius: 12,
            withBorder: true,
            shadow: "none",
            px: "sm",
          }}
          withEditor
          redVariables
        />
      );
    }
    if (selected?.data.actionType === "smv") {
      return <SmvPreviewer model_id={selected?.data?.model_id} />;
    }
    if (selected?.data.actionType === "linkedin_vocal") {
      return <MessageVocalPreviewer model_id={selected?.data?.model_id} />;
    }
    if (selected?.data.actionType === "linkedin_invitation" && !selected?.data?.model_id) {
      return <Blockquote p={"sm"}>Vous avez choisi d’envoyer une invitation linkedin sans message</Blockquote>
    } else {
      return (
        <MessagePreviewer
          model_id={selected?.data?.model_id}
          iframeHeight={"100%"}
          type={selected?.data?.actionType}
          contact={contact}
          paperStyle={{
            radius: 12,
          }}
        />
      );
    }
  }, [selected, contact]);

  const [loading, setLoading] = useState(false);
  useEffect(() => {
    let contactLists = programData?.contactlist_ids;
    if (contactLists?.length > 0) {
      let list = contactLists[0];
      setLoading(true);
      getFirstPageContacts({ id: list, per_page: 5 })
        .then((res) => {
          let results = res.data.results;
          if (results.length > 0) {
            setContact(res.data.results?.[0]);
            setContacts(res.data.results);
          }
        })
        .finally(() => setLoading(false));
    }
  }, [programData?.contactlist_ids]);

  // When there is more than one initial action
  useEffect(() => {
    let contactLists = programData?.contactlist_ids;
    let list = contactLists[0];

    if (branchedEventNodes.length > 1) {
      let branchIndex = branchedActionNodes.findIndex((branch) =>
        branch.some((n) => n.id === selected?.id),
      );
      let eventParent = branchedEventNodes?.[branchIndex]?.[0];

      if (contactLists?.length > 0) {
        setLoading(true);
        getFirstPageContacts({
          id: list,
          per_page: 5,
          filter: eventParent?.data?.filter,
        })
          .then((res) => {
            let results = res.data.results;
            if (results.length > 0) {
              setContact(res.data.results?.[0]);
              setContacts(res.data.results);
            }
          })
          .finally(() => setLoading(false));
      }
    }
  }, [
    programData?.contactlist_ids,
    selected,
    branchedActionNodes,
    branchedEventNodes,
  ]);

  useEffect(() => {
    setTimeout(() => {
      setSelected(branchedActionNodes[0][0]);
    }, 500);
  }, [branchedActionNodes]);

  return (
    <Stack>
      <Group mt={"xl"} w={"85%"} mx={"auto"}>
        <Text fw={700} fz={24}>
          {lang.campaign.review.title}
        </Text>
      </Group>

      <Group w={"85%"} mx={"auto"} align="stretch" h={"75vh"} wrap="nowrap">
        {branchedActionNodes.map((branch, i) => (
          <Stack key={i} h={"100%"} w={96} justify="center">
            <Paper
              w={"100%"}
              align="center"
              justify="center"
              radius={16}
              withBorder
              pos={"relative"}
              flex={"1 1 auto"}
              p={"md"}
              style={{ boxShadow: "#00000012 0 1px 4px", overflow: "auto" }}
            >
              {branchedActionNodes.length > 1 && (
                <Text fw={600} mb={8} size="lg">
                  B{i + 1}
                </Text>
              )}

              {branch.map((item) => {
                let props = getNodeDetails(item.data?.actionType, { size: 32 });
                return (
                  <Tooltip
                    position="right"
                    key={item.id}
                    label={`#${item.id} ${props.label}`}
                  >
                    <Button
                      key={item.id}
                      color={props.color}
                      onClick={() => setSelected(item)}
                      variant={selected?.id === item.id ? "filled" : "light"}
                      size={"xl"}
                      radius={"md"}
                      miw={0}
                      w={48}
                      p={0}
                      h={48}
                      mb={16}
                      styles={{ label: { overflow: "visible" } }}
                    >
                      {props.icon}
                    </Button>
                  </Tooltip>
                );
              })}
            </Paper>
          </Stack>
        ))}

        <Paper
          component={Stack}
          withBorder
          gap={"md"}
          flex={"1 1 auto"}
          radius={16}
          p={"lg"}
          style={{ boxShadow: "#00000012 0 1px 4px" }}
        >
          {branchedActionNodes.length === 0 && (
            <Text c={"red"} size="md" ta={"center"} fw={600}>
              {lang.campaign.review.no_node}
            </Text>
          )}
          {selected?.data?.actionType !== "smv" &&
            selected?.data?.actionType !== "sms" && (
              <SenderVisual data={selected?.data} inReview />
            )}
          <Group wrap="nowrap">
            <Text truncate w={"50%"}>
              {lang.campaign.review.list} :{" "}
              {programData?.contactlist_ids?.[0] ? (
                <Anchor
                  href={`/contact-lists/${programData?.contactlist_ids[0]}`}
                  target="_blank"
                >
                  #{programData?.contactlist_ids[0]} -{" "}
                  {
                    lists?.find(
                      (x) => x.id === programData?.contactlist_ids?.[0],
                    )?.name
                  }
                </Anchor>
              ) : (
                lang.campaigns.no_list_selected
              )}
            </Text>
            <ContactPreview
              loading={loading}
              contact={contact}
              contacts={contacts}
              setContact={setContact}
              actionType={selected?.data?.actionType}
            />
          </Group>
          {selected && <Stack flex={1}>{preview}</Stack>}
        </Paper>
      </Group>
    </Stack>
  );
};

export default Review;
